import { css } from '@emotion/react'
import { useAngularServices } from '@/react/components'
import { useState, useEffect, useRef } from 'react'
import {
  navTab,
  thirdLevelNavTab,
  secondLevelNavTab,
} from '@screens/Navigation/types'
import { useGetNavData } from '@screens/Navigation/hooks/useGetNavData'
import * as React from 'react'
import { Level1Tab } from '@screens/Navigation/components/Level1Tab'
import { UISref } from '@/react/components/UISref'
import { useRouter } from '@/react/hooks'

import HamburgerMenu from './icons/HamburgerMenu.svg'
import classNames from 'classnames'
import LogoutIcon from './componentIcons/LogoutIcon'
import { CurrentUserType } from '@/react/types/documentation'
import HamburgerButton from './components/HamburgerButton'
import type { IScope } from 'angular'

export const MinimizedContext = React.createContext(false)

export const Navigation = () => {
  const angularServices = useAngularServices()
  const $rootScope = angularServices.$rootScope as IScope
  const CurrentUser = angularServices.CurrentUser as {
    getInstance: () => CurrentUserType
  }
  const { stateService } = useRouter()
  const [currentRoute, setCurrentRoute] = useState<string>(
    stateService.current.name,
  )

  const [minimized, setMinimized] = useState<boolean>(() => {
    const savedState = localStorage.getItem('sidebar-minimized')
    if (savedState !== null) {
      return savedState === 'true'
    }
    return window.innerWidth < 768
  })
  const [user] = useState<CurrentUserType>(CurrentUser.getInstance())
  const logoUrl = (CurrentUser as CurrentUserType).getClientCompany().logo_url
  const [logo] = useState<string>(typeof logoUrl === 'string' ? logoUrl : '')
  const [openTabIndex, setOpenTabIndex] = useState<number | null>(() => {
    const savedIndex = localStorage.getItem('openTabIndex')
    return savedIndex !== null ? parseInt(savedIndex) : null
  })
  const [openPopupIndex, setOpenPopupIndex] = useState<number | null>(null)
  const sidebarRef = useRef<HTMLDivElement | null>(null)

  const [navData] = useState<navTab[] | null>(useGetNavData())

  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  const baseStyle = getBaseStyle(variableColor, minimized)

  useEffect(() => {
    localStorage.setItem('sidebar-minimized', minimized.toString())
  }, [minimized])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setMinimized(true)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    document.body.style.setProperty(
      '--sidebar-width',
      minimized ? '77px' : '240px',
    )
  }, [minimized])

  const isTabActive = (
    tab: navTab | secondLevelNavTab | thirdLevelNavTab | null,
    currentRoute: string,
  ): boolean => {
    if (!tab) return false

    if (tab.link === currentRoute) {
      return true
    }

    if (tab.active && tab.active.includes(currentRoute)) {
      return true
    }

    return false
  }

  useEffect(() => {
    if (openTabIndex !== null) {
      localStorage.setItem('openTabIndex', openTabIndex.toString())
    } else {
      localStorage.removeItem('openTabIndex')
    }
  }, [openTabIndex])

  useEffect(() => {
    const unsubscribe = $rootScope.$on(
      '$stateChangeSuccess',
      (_event: unknown, toState: { name: string }) => {
        setCurrentRoute(toState.name)
      },
    )
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe()
    }
  }, [$rootScope])

  return (
    <MinimizedContext.Provider value={minimized}>
      <section ref={sidebarRef} css={baseStyle}>
        <div
          className={classNames('body', {
            'body-minimized': minimized,
            'body-full': !minimized,
          })}
        >
          {minimized && (
            <HamburgerButton
              image={HamburgerMenu}
              onClick={() => {
                setMinimized(!minimized)
              }}
            />
          )}
          {!minimized && (
            <>
              <div className="account-display">
                <div className="logo-row">
                  <img className="logo" src={logo || ''} alt={''} />
                  <HamburgerButton
                    image={HamburgerMenu}
                    onClick={() => {
                      setMinimized(!minimized)
                    }}
                  />
                </div>
                <span className="user-data">
                  <div>
                    <strong className="font-bold">
                      {user.first_name} {user.last_name}
                    </strong>
                  </div>
                  <div>{user.title || 'No Title'}</div>
                </span>
              </div>
            </>
          )}
          <div className="navigation-table">
            {navData?.map((tab, index) => {
              const isThisTabActive = isTabActive(tab, currentRoute)
              const isOpen = openTabIndex === index

              return (
                <Level1Tab
                  key={`${index}-${tab.text}`}
                  tabData={tab}
                  isOpen={isOpen}
                  isActive={isThisTabActive}
                  onClose={() => setOpenTabIndex(null)}
                  onClick={() => {
                    if (openTabIndex !== index) {
                      setOpenTabIndex(index)
                    } else {
                      setOpenTabIndex(null)
                    }
                  }}
                  sidebarRef={sidebarRef}
                  openPopupIndex={openPopupIndex}
                  setOpenPopupIndex={setOpenPopupIndex}
                  currentIndex={index}
                  currentRoute={currentRoute}
                />
              )
            })}
            <div className="logout-container">
              <UISref to="logout">
                <a className="ccs-link logout-button">
                  <LogoutIcon />
                  {!minimized && <span>Log out</span>}
                </a>
              </UISref>
            </div>
          </div>
        </div>
      </section>
    </MinimizedContext.Provider>
  )
}

const getBaseStyle = (variableColor: string, minimized: boolean) =>
  css({
    display: 'flex',
    width: 'fit-content',
    height: '100svh',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
    backgroundColor: variableColor,
    '@media print': {
      display: 'none',
    },
    '.body': {
      position: 'static',
      transition: 'all 0.3s ease',
      '.hamburger-button-container': {
        display: 'flex',
        justifyContent: minimized ? 'center' : 'flex-end',
        padding: !minimized ? '0px' : '15px 10px',
      },
      '.hamburger-button': {
        width: 'fit-content',
        height: 'fit-content',
        padding: '10px 15px',
        cursor: 'pointer',
        borderRadius: 5,
        ':hover': {
          backgroundColor: 'rgba(0,0,0,0.2)',
        },
      },
      '.logo-row': {
        marginTop: 24,
        paddingLeft: 20,
        paddingRight: 24,
        width: '100%',
        height: 70,
        display: 'flex',
        justifyContent: 'space-between',
        '.logo': {
          height: '100%',
          width: 'auto',
          maxWidth: 120,
          objectFit: 'contain',
        },
      },
      '.user-data': {
        marginLeft: 20,
        marginBottom: 10,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        div: {
          width: '90%',
        },
      },
      '.navigation-table': {
        display: 'flex',
        flexDirection: 'column',
        height: minimized ? 'calc(100% - 85px)' : 'calc(100% - 155px)',
      },
      '.logout-container': {
        display: 'flex',
        marginTop: 'auto',
        padding: minimized ? '12px 16px' : '12px 30px',
        justifyContent: minimized ? 'center' : 'flex-end',

        '.logout-button': {
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          textDecoration: 'none',
          gap: '12px',

          '&:hover': {
            opacity: 0.8,
          },

          span: {
            color: 'white',
          },
        },
      },
    },
    '.body-full': {
      width: 240,
      overflowY: 'scroll',
      transition: '500ms',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    '.body-minimized': {
      transition: '500ms',
      maxWidth: 77,
      overflowY: 'scroll',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    '.display-segment-icon': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '.display-segment-2': {
      display: 'flex',
      alignItems: 'center',
    },
  })
